import { apiClient } from "../../../../lib/axios";

export type PreRegistrationStatus = "pending" | "interest" | "rejected";

export type PreRegistrationEntry = {
  email: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  interest_note?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  submitted_at?: string | null;
  status?: PreRegistrationStatus;
};

export type PreRegistrationListResponse = {
  data: PreRegistrationEntry[];
};

export type PreRegistrationAnswersResponse = {
  data: {
    email: string;
    client_id?: string | null;
    client_name?: string | null;
    answers: Array<{
      question_id: string;
      title: string;
      type: string;
      answer: string | string[] | number | null;
    }>;
  };
};

export type PreRegistrationNotificationRecipient = {
  id: string;
  client_id?: string | null;
  client_name?: string | null;
  name?: string | null;
  email: string;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type PreRegistrationNotificationRecipientsResponse = {
  data: PreRegistrationNotificationRecipient[];
};

export const preRegistrationsApi = {
  list: async (
    clientId?: string | null,
    status: PreRegistrationStatus = "pending"
  ): Promise<PreRegistrationListResponse> => {
    const response = await apiClient.get("/admin/pre-registration", {
      params: {
        ...(clientId ? { client_id: clientId } : {}),
        status,
      },
    });
    return response.data;
  },

  getAnswers: async (email: string): Promise<PreRegistrationAnswersResponse> => {
    const response = await apiClient.get(
      `/admin/pre-registration/${encodeURIComponent(email)}/answers`
    );
    return response.data;
  },

  approve: async (email: string): Promise<{ status: string }> => {
    const response = await apiClient.post("/admin/pre-registration/approve", {
      email,
    });
    return response.data;
  },

  reject: async (
    email: string
  ): Promise<{ status: string; email_sent?: boolean; email_error?: string | null }> => {
    const response = await apiClient.post("/admin/pre-registration/reject", {
      email,
    });
    return response.data;
  },

  listNotificationRecipients: async (params?: {
    scope?: "global" | "client" | "all";
    client_id?: string | null;
  }): Promise<PreRegistrationNotificationRecipientsResponse> => {
    const query: Record<string, string> = {};
    if (params?.scope && params.scope !== "all") query.scope = params.scope;
    if (params?.client_id) query.client_id = params.client_id;
    const response = await apiClient.get(
      "/admin/pre-registration/notification-recipients",
      { params: Object.keys(query).length ? query : undefined }
    );
    return response.data;
  },

  createNotificationRecipient: async (payload: {
    email: string;
    name?: string;
    client_id?: string | null;
  }): Promise<{ data: PreRegistrationNotificationRecipient }> => {
    const response = await apiClient.post(
      "/admin/pre-registration/notification-recipients",
      payload
    );
    return response.data;
  },

  deleteNotificationRecipient: async (
    recipientId: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete(
      `/admin/pre-registration/notification-recipients/${recipientId}`
    );
    return response.data;
  },
};
